import { useEffect, useRef, useState } from "react";
import { Loader2, BookOpen, ExternalLink } from "lucide-react";

/**
 * Soro blog embed.
 *
 * Injects the trysoro.com embed script exactly once. The script targets
 * a div with id="soro-blog" and populates it with blog articles.
 *
 * If the embed doesn't populate within ~4 seconds (e.g. the Soro account
 * is disabled or has no articles yet), we show a friendly empty state
 * so the page still feels intentional.
 */
const SORO_SCRIPT_SRC =
    "https://app.trysoro.com/api/embed/3dc476c9-901c-4840-a635-3201ea451a49";
const SORO_SCRIPT_ID = "soro-blog-embed-script";
const READY_CHECK_MS = 4500;

export const SoroBlog = () => {
    const containerRef = useRef(null);
    const [state, setState] = useState("loading"); // loading | ready | empty

    useEffect(() => {
        // If script already present, don't add again — just wait for content.
        if (!document.getElementById(SORO_SCRIPT_ID)) {
            const s = document.createElement("script");
            s.src = SORO_SCRIPT_SRC;
            s.defer = true;
            s.async = true;
            s.id = SORO_SCRIPT_ID;
            s.crossOrigin = "anonymous";
            document.body.appendChild(s);
        }

        // After a fair delay, check whether Soro actually rendered content.
        const t = setTimeout(() => {
            const target = document.getElementById("soro-blog");
            const rendered =
                !!target &&
                (target.children.length > 0 ||
                    target.innerHTML.trim().length > 0);
            setState(rendered ? "ready" : "empty");
        }, READY_CHECK_MS);

        return () => clearTimeout(t);
    }, []);

    return (
        <div
            ref={containerRef}
            data-testid="soro-blog-container"
            className="soro-blog-container"
        >
            {state === "loading" && (
                <div
                    data-testid="soro-blog-loading"
                    className="flex items-center justify-center gap-2 py-16 text-tp-ink/60"
                >
                    <Loader2 size={18} className="animate-spin text-tp-forest" />
                    <span className="text-sm">Loading articles…</span>
                </div>
            )}

            {/* Soro will inject content into this div */}
            <div id="soro-blog" className="soro-target min-h-[80px]"></div>

            {state === "empty" && (
                <div
                    data-testid="soro-blog-empty"
                    className="mt-2 rounded-2xl border border-tp-forest/10 bg-white p-6 md:p-8 text-center shadow-[0_10px_30px_rgba(20,83,45,0.06)]"
                >
                    <div className="mx-auto inline-flex items-center justify-center h-12 w-12 rounded-full bg-tp-forest/5 ring-1 ring-tp-gold/40">
                        <BookOpen
                            size={22}
                            strokeWidth={2.2}
                            className="text-tp-forest"
                        />
                    </div>
                    <h3 className="mt-4 font-heading text-2xl md:text-3xl text-tp-forest leading-tight">
                        Fresh articles coming soon
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-tp-ink/70 max-w-md mx-auto">
                        We&rsquo;re putting the finishing touches on our first
                        guides &mdash; skip bin sizes, permit tips, and Sydney
                        clean-up advice. Check back shortly.
                    </p>
                    <a
                        href="https://app.trysoro.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 text-xs text-tp-forest/60 hover:text-tp-forest"
                    >
                        Powered by Soro
                        <ExternalLink size={12} />
                    </a>
                </div>
            )}
        </div>
    );
};

export default SoroBlog;
